import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

type Mode = 'email' | 'newPassword' | 'invalid';

const log = (...args: any[]) => console.log('[ForgotPassword]', ...args);

const post = async (path: string, body: object) => {
    const url = `${API_BASE_URL}${path}`;
    log('POST', url, { ...body, token: (body as any).token ? '<token>' : undefined, new_password: undefined, confirm_password: undefined });
    const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    log('RESPONSE', path, res.status, data);
    return { ok: res.ok && data.success !== false, data };
};

// Styles ported from Style/ForgotPassword.css (only the rules this page uses), scoped under .fp-root
const css = `
.fp-root, .fp-root * { box-sizing: border-box; }
.fp-root p { margin: 0; padding: 0; font-family: Arial, Helvetica, sans-serif; }

.fp-root #divForgotPassword {
    height: 100vh;
    width: 100vw;
    background-color: rgba(40, 40, 40, 0.995);
    position: fixed;
    top: 0;
    left: 0;
    overflow-y: auto;
    z-index: 9999999999999;
    text-align: center;
    font-family: 'Open Sans', sans-serif;
}
.fp-root #divForgotPassword p {
    height: auto;
    width: 60%;
    margin: 50px auto;
    padding: 0;
    display: block;
    font-size: 17px;
    line-height: 25px;
    color: #FFFFFF;
    margin-bottom: 0px;
}

.fp-root .title_text {
    font-family: 'Montserrat', sans-serif;
    font-size: 45px;
    line-height: 60px;
    color: #FFF;
    width: 100%;
    display: block;
    margin-top: 60px;
    text-align: center;
    text-shadow: 3px 1px 3px rgba(0, 0, 0, 0.4);
}
.fp-root span#ar_text { display: block; margin-top: 20px; }
.fp-root span#ar_text.fp-error { color: #ffb27e; }

.fp-root .button_box {
    width: 100%;
    height: 150px;
    display: table;
    padding: 0;
    text-align: center;
    margin-top: 0;
}
.fp-root .general_btn {
    height: 80px;
    width: 99%;
    display: inline-table;
    border: none;
    background-color: #F60;
    padding: 0;
    -webkit-box-shadow: 0px 2px 2px 2px rgba(0,0,0,0.15);
    box-shadow: 0px 2px 2px 2px rgba(0,0,0,0.15);
    margin: 30px auto;
    margin-bottom: 30px;
    cursor: pointer;
    text-decoration: none;
}
.fp-root .general_btn:hover { background-color: #F60; border: none; }
.fp-root .general_btn.fp-busy { opacity: 0.6; pointer-events: none; }
.fp-root .general_btn span {
    text-decoration: none;
    outline: none;
    padding: 0 15px 0 15px;
    margin: 0;
    height: 100%;
    width: 100%;
    font-size: 20px;
    font-weight: bold;
    color: #FFF;
    text-align: center;
    display: table-cell;
    vertical-align: middle;
}
.fp-root .two_btn_lightbox { width: 30%; }
.fp-root .two_btn_left { margin-right: 25px; }

.fp-root .login_field {
    margin: 0 auto;
    -webkit-transition: all 0.30s ease-in-out;
    transition: all 0.30s ease-in-out;
    outline: none;
    box-sizing: border-box;
    background: #FCFCFC;
    margin-bottom: 20px;
    border: 1px solid #ccc;
    padding: 25px;
    display: inline-block;
    font-size: 18px;
    text-align: left;
    color: #535353;
    width: 30%;
}
.fp-root .login_field:focus {
    box-shadow: 0 0 5px #1e5da8;
    border: 1px solid #676767;
    background-color: #FFF;
}
.fp-root .login_fp { margin-top: 0; }

@media screen and (max-width:1400px) {
    .fp-root .title_text { font-size: 30px; line-height: 30px; margin-top: 20px; }
    .fp-root .button_box { height: auto; }
    .fp-root .general_btn { height: 80px; margin-top: 20px; margin-bottom: 20px; }
    .fp-root .login_field { margin-bottom: 15px; padding: 15px; font-size: 13px; width: 40%; padding-left: 30px; }
}
@media screen and (max-width:1000px) {
    .fp-root .login_field { width: 60%; }
}
@media screen and (max-width:800px) {
    .fp-root .login_field { margin-bottom: 15px; padding: 15px; font-size: 13px; width: 60%; padding-left: 30px; }
}
@media screen and (max-width:768px) {
    .fp-root .title_text { margin: 0 auto; width: 60%; }
}
@media screen and (max-width:650px) {
    .fp-root .title_text { font-size: 30px; line-height: 30px; margin-top: 15px; }
}
@media screen and (max-width:590px) {
    .fp-root #divForgotPassword p { width: 70%; }
    .fp-root .two_btn_left { margin-right: 20px; }
    .fp-root .two_btn_lightbox { width: 40%; }
}
@media screen and (max-width:375px) {
    .fp-root .title_text { font-size: 25px; line-height: 30px; margin: 15px auto; width: 80%; }
    .fp-root .login_field { width: 80%; }
    .fp-root #divForgotPassword p { width: 80%; font-size: 14px; line-height: 15px; margin: 0 auto; }
    .fp-root .general_btn { margin-top: 12px; }
    .fp-root .two_btn_left { margin-right: 15px; }
    .fp-root .two_btn_lightbox { width: 42%; }
}
`;

// Inline copy of the overlay's key styles so global app CSS cannot hide the page
const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    background: '#282828',
    color: '#fff',
    overflowY: 'auto',
    zIndex: 2147483647,
    textAlign: 'center',
    display: 'block',
    visibility: 'visible',
    opacity: 1,
};

const ForgotPassword: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const userType = searchParams.get('UserType') || 'FamilyLogin';
    const token = searchParams.get('token') || '';
    const overlayRef = useRef<HTMLDivElement | null>(null);

    const [mode, setMode] = useState<Mode>(token ? 'newPassword' : 'email');
    const [checkingLink, setCheckingLink] = useState<boolean>(!!token);
    const [email, setEmail] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('');
    const [isError, setIsError] = useState(false);
    const [done, setDone] = useState(false);
    const [busy, setBusy] = useState(false);

    log('render', { mode, checkingLink, hasToken: !!token, userType, done, busy, API_BASE_URL });

    // log what the overlay really looks like in the browser after every render
    useEffect(() => {
        const el = overlayRef.current;
        if (!el) { log('overlay element NOT found'); return; }
        const r = el.getBoundingClientRect();
        const cs = window.getComputedStyle(el);
        log('overlay mounted', {
            rect: { x: r.x, y: r.y, w: r.width, h: r.height },
            display: cs.display, position: cs.position, visibility: cs.visibility,
            opacity: cs.opacity, zIndex: cs.zIndex, background: cs.backgroundColor,
            parentIsBody: el.parentElement?.parentElement === document.body,
        });
    });

    const defaultText = mode === 'email'
        ? 'Please enter the email address you used when you signed up.'
        : 'Please enter your New Password.';

    const showError = (text: string) => { setMessage(text); setIsError(true); };
    const showInfo = (text: string) => { setMessage(text); setIsError(false); };

    // validate the reset link once, when the page opens from the mail
    useEffect(() => {
        log('mounted. token present:', !!token, 'API_BASE_URL:', API_BASE_URL);
        if (!token) return;
        (async () => {
            try {
                const { ok, data } = await post('api/forgot-password/validate/', { token });
                if (!ok) {
                    log('link invalid', data);
                    setMode('invalid');
                    showError(data.error || 'This reset link is invalid or has expired.');
                } else {
                    log('link valid');
                }
            } catch (err) {
                console.error('[ForgotPassword] validate failed', err);
                setMode('invalid');
                showError('Could not check the reset link. Please try again.');
            } finally {
                setCheckingLink(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const goToLogin = () => { log('navigate to /Login'); navigate('/Login'); };

    const submit = async () => {
        log('submit clicked', { mode, busy });
        if (busy) return;
        setMessage('');
        setIsError(false);

        if (mode === 'email') {
            if (!email.trim()) { showError('Please enter your email address.'); return; }
            setBusy(true);
            try {
                const { ok, data } = await post('api/forgot-password/request/', { email: email.trim(), user_type: userType });
                if (ok) { showInfo(data.message); setDone(true); }
                else { showError(data.error || 'Something went wrong.'); setEmail(''); }
            } catch (err) { console.error('[ForgotPassword] request failed', err); showError('Network error. Please try again.'); }
            finally { setBusy(false); }
            return;
        }

        if (!newPassword) { showError('Please enter your New Password.'); return; }
        if (newPassword !== confirmPassword) { showError('New Password and Confirm Password do not match.'); return; }
        setBusy(true);
        try {
            const { ok, data } = await post('api/forgot-password/reset/', {
                token, new_password: newPassword, confirm_password: confirmPassword,
            });
            if (ok) { showInfo(data.message); setDone(true); }
            else showError(data.error || 'Something went wrong.');
        } catch (err) { console.error('[ForgotPassword] reset failed', err); showError('Network error. Please try again.'); }
        finally { setBusy(false); }
    };

    const onEnter = (e: React.KeyboardEvent) => { if (e.key === 'Enter') submit(); };
    const click = (fn: () => void) => (e: React.MouseEvent) => { e.preventDefault(); fn(); };

    if (checkingLink) {
        return createPortal(
            <div className="fp-root">
                <style>{css}</style>
                <div id="divForgotPassword" ref={overlayRef} style={overlayStyle}>
                    <p>Checking link…</p>
                </div>
            </div>,
            document.body
        );
    }

    return createPortal(
        <div className="fp-root">
            <style>{css}</style>
            <div id="divForgotPassword" ref={overlayRef} style={overlayStyle}>
                {mode === 'email' && (
                    <div id="divEMail">
                        <p>
                            <span className="title_text">Account Recovery</span>
                            <span id="ar_text" className={isError ? 'fp-error' : ''} style={{fontWeight:"normal"}}>{message || defaultText}</span>
                            <br />
                            {!done && (
                                <span id="fp_form_box">
                                    <input
                                        className="login_field login_fp"
                                        type="text"
                                        placeholder="Email"
                                        maxLength={50}
                                        spellCheck={false}
                                        autoCorrect="off"
                                        tabIndex={1}
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        onKeyDown={onEnter}
                                    />
                                </span>
                            )}
                        </p>
                    </div>
                )}

                {mode !== 'email' && (
                    <div id="divNewPassword">
                        <p>
                            <span className="title_text" style={{fontWeight:"normal", marginBottom:"20px"}}>Reset Password</span>
                            <span id="ar_text" className={isError ? 'fp-error' : ''} style={{fontWeight:"normal"}}>
                                {message || (mode === 'invalid' ? '' : defaultText)}
                            </span>
                            <br />
                            {!done && mode === 'newPassword' && (
                                <>
                                    <span id="fp_form_NPwd" style={{fontWeight:"normal"}}>New Password<br />
                                        <input
                                            className="login_field"
                                            type="password"
                                            maxLength={30}
                                            tabIndex={1}
                                            autoComplete="new-password"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                        />
                                    </span>
                                    <br />
                                    <span id="fp_form_CPwd" style={{fontWeight:"normal"}}>Confirm Password<br />
                                        <input
                                            className="login_field"
                                            type="password"
                                            maxLength={30}
                                            tabIndex={2}
                                            autoComplete="new-password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            onKeyDown={onEnter}
                                        />
                                    </span>
                                </>
                            )}
                        </p>
                    </div>
                )}

                <div className="button_box">
                    {done || mode === 'invalid' ? (
                        <a href="#" className="general_btn two_btn_lightbox" onClick={click(goToLogin)}>
                            <span>OK</span>
                        </a>
                    ) : (
                        <>
                            <a href="#" className="general_btn two_btn_lightbox two_btn_left" onClick={click(goToLogin)}>
                                <span>Cancel</span>
                            </a>
                            <a href="#" className={`general_btn two_btn_lightbox${busy ? ' fp-busy' : ''}`} onClick={click(submit)}>
                                <span>Submit</span>
                            </a>
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ForgotPassword;

/* Route:  <Route path="/LOGIN/ForgotPassword" element={<ForgotPassword />} /> */